export interface CursorModelParam {
  id: string;
  value: string;
}

export interface CursorCatalogLevel {
  id: string;
  label: string;
  params: CursorModelParam[];
}

export interface CursorCatalogModel {
  id: string;
  label: string;
  levels: CursorCatalogLevel[];
}

export interface CursorSdkModel {
  id?: string;
  displayName?: string;
  parameters?: Array<{
    id?: string;
    displayName?: string;
    values?: Array<{ value?: string; displayName?: string }>;
  }>;
  variants?: Array<{
    displayName?: string;
    isDefault?: boolean;
    params?: Array<{ id?: string; value?: string }>;
  }>;
}

const LEVEL_PARAM_IDS = new Set(['effort', 'reasoning', 'thinking']);

function slug(value: string): string {
  return (
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'level'
  );
}

function variantParams(variant: { params?: Array<{ id?: string; value?: string }> }): CursorModelParam[] {
  return Array.isArray(variant.params)
    ? variant.params.filter((param): param is CursorModelParam => Boolean(param.id && param.value))
    : [];
}

function levelParam(model: CursorSdkModel) {
  const parameters = Array.isArray(model.parameters) ? model.parameters : [];
  return (
    parameters.find((parameter) => LEVEL_PARAM_IDS.has(String(parameter.id || '').trim().toLowerCase())) ||
    parameters.find((parameter) => String(parameter.id || '').trim().toLowerCase() === 'fast')
  );
}

function parameterLabel(
  parameter: { id?: string; displayName?: string; values?: Array<{ value?: string; displayName?: string }> },
  raw: string,
): string {
  const match = (parameter.values || []).find((value) => String(value.value || '').trim() === raw);
  const named = String(match?.displayName || '').trim();
  if (named) return named;
  if (raw === 'false') return 'Default';
  if (raw === 'true') return String(parameter.displayName || 'On').trim() || 'On';
  return raw;
}

function variantsHaveDistinctNames(label: string, variants: Array<{ displayName?: string }>): boolean {
  return variants.some((variant) => {
    const name = String(variant.displayName || '').trim();
    return Boolean(name) && name !== label;
  });
}

function pickVariantForValue(
  variants: Array<{ displayName?: string; isDefault?: boolean; params?: Array<{ id?: string; value?: string }> }>,
  parameterId: string,
  raw: string,
): { params?: Array<{ id?: string; value?: string }>; isDefault?: boolean } | undefined {
  const matching = variants.filter((variant) =>
    variantParams(variant).some((param) => param.id === parameterId && param.value === raw),
  );
  if (matching.length === 0) return undefined;
  const defaultVariant = variants.find((variant) => variant.isDefault);
  const defaultParams = defaultVariant ? variantParams(defaultVariant) : [];
  const matchingDefault = matching.find((variant) => variant.isDefault);
  if (matchingDefault) return matchingDefault;
  const aligned = matching.find((variant) =>
    defaultParams.every(
      (param) =>
        param.id === parameterId ||
        variantParams(variant).some((row) => row.id === param.id && row.value === param.value),
    ),
  );
  return aligned || matching[0];
}

function levelsFromParameter(
  parameter: NonNullable<ReturnType<typeof levelParam>>,
  variants: Array<{ displayName?: string; isDefault?: boolean; params?: Array<{ id?: string; value?: string }> }>,
): CursorCatalogLevel[] {
  const parameterId = String(parameter.id).trim();
  const levels = (parameter.values || [])
    .map((value) => {
      const raw = String(value.value || '').trim();
      const picked = pickVariantForValue(variants, parameterId, raw);
      return {
        id: raw,
        label: parameterLabel(parameter, raw),
        params: picked ? variantParams(picked) : raw ? [{ id: parameterId, value: raw }] : [],
      };
    })
    .filter((level) => level.id);

  const defaultRaw = variantParams(variants.find((variant) => variant.isDefault) || {}).find(
    (param) => param.id === parameterId,
  )?.value;
  if (!defaultRaw) return levels;
  const defaultIndex = levels.findIndex((level) => level.id === defaultRaw);
  if (defaultIndex <= 0) return levels;
  return [levels[defaultIndex], ...levels.filter((_, index) => index !== defaultIndex)];
}

export function catalogModelFromSdk(model: CursorSdkModel): CursorCatalogModel {
  const id = String(model.id || '').trim();
  const label = String(model.displayName || id).trim() || id;
  const variants = Array.isArray(model.variants) ? model.variants : [];
  const parameter = levelParam(model);

  if (variants.length > 0 && variantsHaveDistinctNames(label, variants)) {
    return {
      id,
      label,
      levels: variants.map((variant, index) => {
        const variantLabel = String(variant.displayName || '').trim() || `Level ${index + 1}`;
        return {
          id: slug(variantLabel) || `variant-${index}`,
          label: variantLabel,
          params: variantParams(variant),
        };
      }),
    };
  }

  if (parameter && Array.isArray(parameter.values) && parameter.values.length > 0) {
    return {
      id,
      label,
      levels: levelsFromParameter(parameter, variants),
    };
  }

  return { id, label, levels: [] };
}

export function resolveCatalogModelParams(
  models: CursorCatalogModel[],
  modelId: string,
  levelId?: string,
): CursorModelParam[] {
  const model = models.find((row) => row.id === modelId);
  if (!model) return [];
  const selected = String(levelId || '').trim();
  if (!selected) return model.levels[0]?.params || [];
  const level = model.levels.find((row) => row.id === selected);
  return level?.params || [];
}

export function cursorCatalogModelId(id: string): string {
  const raw = String(id || '').trim();
  if (!raw) return '';
  return raw.startsWith('cursor:') ? raw.slice('cursor:'.length) : raw;
}
